import { FC, useCallback, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import {
  Icon,
  IconButton,
  RyeconCaretDown,
  RyeconCaretRight,
  RyeconKey,
} from '@dbt-labs/sourdough';

import { Description } from '@/components/ResourcePanel/ResourcePanels/Description';
import { useDbtCloudAccount } from '@/context/DbtCloudProvider';
import { AppliedColumn, ColumnTest } from '@/hooks/appliedColumns';
import { useValidatedParams } from '@/hooks/params';
import { useScrollToElement } from '@/hooks/useScrollToElement';
import { BoldedText } from '@/pages/account/project/search/components/BoldedText';
import { truthy } from '@/util/array';
import { Badge, Badges } from './Badge';
import { NO_DESCRIPTION } from './DescriptionForm';
import { ColumnLineageView } from './Lineage/ColumnLineageView';
import { useCurrentPage } from './Page';
import { TestStatusRows } from './TestStatusRow';

type ColumnCardProps = Omit<
  AppliedColumn,
  'tests' | 'descriptionOriginColumnName' | 'descriptionOriginResourceUniqueId'
> & {
  descriptionOriginColumnName?: string | null;
  descriptionOriginResourceUniqueId?: string | null;
  tests: ColumnTest[] | undefined | null;
  metaMatch?: string;
  isPrimaryKey: boolean;
  shouldShowLineage?: boolean;
};

export const ColumnCard: FC<ColumnCardProps> = ({
  name,
  type,
  description,
  descriptionOriginColumnName,
  descriptionOriginResourceUniqueId,
  meta = {},
  metaMatch,
  tests,
  constraints,
  isPrimaryKey,
  shouldShowLineage = true,
}) => {
  const { uniqueId } = useValidatedParams();
  const { accountId, hasAccountFeature } = useDbtCloudAccount();
  const shouldShowColumnLevelLineage =
    shouldShowLineage && hasAccountFeature('explorerCll');
  const hasMeta = Object.keys(meta).length !== 0;

  const location = useLocation();
  const { setLineageIngressPath } = useCurrentPage();
  const [searchParams, setSearchParams] = useSearchParams();

  const [isSelected, setSelected] = useState(
    searchParams.get('originColumn')?.toLowerCase() === name?.toLowerCase(),
  );
  const ryecon = isSelected ? RyeconCaretDown : RyeconCaretRight;

  const selectedRef = useRef<HTMLDivElement | null>(null);

  useScrollToElement(selectedRef as React.RefObject<HTMLElement>, {
    enabled: isSelected,
  });

  const onOpenFullScreenLineage = useCallback(() => {
    setSearchParams((searchParams) => {
      // Keep track of which column's lineage view we clicked on
      if (name) {
        searchParams.set('originColumn', name);
      }
      setLineageIngressPath?.(`${location.pathname}?${searchParams.toString()}`);
      return searchParams;
    });
  }, [location.pathname, name, setLineageIngressPath, setSearchParams]);

  const desc = description ?? NO_DESCRIPTION;

  if (!name || !accountId) return null;
  return (
    <li
      data-testid={`${name}-card`}
      key={name}
      aria-label={name}
      className="divide-y divide-borderMuted overflow-hidden rounded-lg border border-borderMuted bg-bgMain text-sm"
    >
      <div className="px-4 py-2">
        <div className="flex items-center text-fgMain">
          {(shouldShowColumnLevelLineage || hasMeta) && (
            <IconButton
              onClick={() => setSelected(!isSelected)}
              className="flex-2 not-sr-only pb-0.5 align-middle"
              testId={`toggle-column-card-${name}`}
              ryecon={ryecon}
              size="xs"
              trackingId="explorer-column-card-toggle"
              tooltip={`${isSelected ? 'Hide' : 'Show'} ${shouldShowColumnLevelLineage ? 'Column Lineage' : 'Custom Metadata'}`}
            />
          )}
          <span className="font-label not-sr-only mx-1 flex-1">
            {name}
            {isPrimaryKey && (
              <>
                <Icon
                  size="xs"
                  testId={`pk-icon-${name}`}
                  className="ml-2 mr-1"
                  ryecon={RyeconKey}
                />
                PK
              </>
            )}
          </span>
          <span className="flex-0">
            <Badge aria-label="data type">{type?.toUpperCase()}</Badge>
          </span>
          <Badges
            content={
              constraints
                ?.map((constraint) => constraint.name ?? constraint.type)
                .filter(truthy) ?? []
            }
          />
        </div>
        {desc && (
          <Description
            description={desc}
            descriptionOriginColumnName={descriptionOriginColumnName}
            descriptionOriginResourceUniqueId={descriptionOriginResourceUniqueId}
          />
        )}
      </div>
      {!isSelected && metaMatch && (
        <div className="mb-4 ml-4 text-fgDecorative">
          Meta Contains:{' '}
          <BoldedText
            text={metaMatch}
            shouldBeBold={metaMatch}
            boldProps={{ className: 'bg-bgBrandMuted' }}
          />
        </div>
      )}
      {tests && <TestStatusRows className="px-4" tests={tests} />}
      {isSelected && (
        <>
          {shouldShowColumnLevelLineage && (
            <div className={'h-96'} data-testid={`${name}-expanded`} ref={selectedRef}>
              <ColumnLineageView
                columnName={name}
                uniqueId={uniqueId?.raw}
                isFullScreen={false}
                onOpenFullScreenLineage={onOpenFullScreenLineage}
              />
            </div>
          )}
          {hasMeta && (
            <div className="mx-4 my-6 flex flex-col space-y-2">
              <span className="mb-2 min-w-[140px] flex-1 truncate whitespace-nowrap text-sm text-fgDecorative">
                Custom Metadata
              </span>
              <div className="w-fit rounded-md border bg-bgNeutralMuted p-4">
                <code className="whitespace-break-spaces text-xs">
                  <BoldedText
                    text={JSON.stringify(meta, null, 2)}
                    shouldBeBold={metaMatch || ''}
                    boldProps={{ className: 'bg-bgBrandMuted' }}
                  />
                </code>
              </div>
            </div>
          )}
        </>
      )}
    </li>
  );
};
